import React from 'react';
import { ChevronLeft, type LucideIcon } from 'lucide-react';
import DateRangeFilter from '../../components/common/DateRangeFilter';
import { ExportMenu, type ExportRow } from '../../lib/exportReports';
import { formatRangeLabel, type DateRangePreset } from '../../lib/dateFilter';
import { useScrollToTop } from '../../lib/useScrollToTop';
import type { DateRange } from '../../lib/dateFilter';

type DashboardDetailShellProps = {
  onClose: () => void;
  eyebrow?: string;
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  iconClassName?: string;
  exportTitle?: string;
  exportFilename?: string;
  exportRows?: ExportRow[];
  headerExtra?: React.ReactNode;
  /** When set, shows date filter and stamps exports with the selected window. */
  dateRange?: DateRange;
  datePreset?: DateRangePreset;
  onDateStart?: (d: Date | null) => void;
  onDateEnd?: (d: Date | null) => void;
  onDatePreset?: (preset: DateRangePreset) => void;
  onDateClear?: () => void;
  children: React.ReactNode;
};

export function DashboardDetailShell({
  onClose,
  eyebrow = 'Dashboard',
  title,
  subtitle,
  icon: Icon,
  iconClassName = 'text-emerald-700',
  exportTitle,
  exportFilename,
  exportRows = [],
  headerExtra,
  dateRange,
  datePreset = 'all',
  onDateStart,
  onDateEnd,
  onDatePreset,
  onDateClear,
  children,
}: DashboardDetailShellProps) {
  useScrollToTop([]);

  const canExport = Boolean(exportTitle && exportFilename);
  const rangeLabel = dateRange ? formatRangeLabel(dateRange) : '';
  const exportTitleFull = rangeLabel && exportTitle ? `${exportTitle} (${rangeLabel})` : exportTitle ?? '';

  return (
    <div className="app-page space-y-6">
      <header className="app-page-header">
        <div className="flex min-w-0 flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <button
              type="button"
              onClick={onClose}
              className="app-btn-secondary inline-flex w-fit shrink-0 items-center gap-2 py-2 pl-3 pr-4 text-sm"
            >
              <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden />
              Back
            </button>
            <div className="flex min-w-0 items-start gap-3">
              {Icon ? (
                <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-zinc-200 bg-white shadow-sm">
                  <Icon className={`h-5 w-5 ${iconClassName}`} aria-hidden />
                </span>
              ) : null}
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-800">{eyebrow}</p>
                <h1 className="app-page-title mt-0.5">{title}</h1>
                {subtitle ? <p className="app-page-sub mt-1">{subtitle}</p> : null}
                {rangeLabel ? <p className="mt-1 text-xs text-zinc-500">Period: {rangeLabel}</p> : null}
              </div>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-stretch gap-2 sm:items-end">
            {dateRange && onDateStart && onDateEnd && onDatePreset && onDateClear ? (
              <DateRangeFilter
                start={dateRange.start}
                end={dateRange.end}
                preset={datePreset}
                onStart={onDateStart}
                onEnd={onDateEnd}
                onPreset={onDatePreset}
                onClear={onDateClear}
                compact
              />
            ) : null}
            <div className="flex flex-wrap items-center justify-end gap-2">
              {headerExtra}
              {canExport ? (
                <ExportMenu
                  title={exportTitleFull}
                  excelFilename={exportFilename!}
                  rows={exportRows}
                  disabled={exportRows.length === 0}
                />
              ) : null}
            </div>
          </div>
        </div>
      </header>
      {children}
    </div>
  );
}

/** Summary metric tile used across dashboard drill-downs. */
export function DashboardMetricCard({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
}) {
  return (
    <div
      className={`app-card shadow-sm ring-1 ring-zinc-950/[0.03] ${
        accent ? 'border-l-[3px] border-l-emerald-600' : ''
      }`}
    >
      <div className="app-card-inner">
        <p className="app-section-title">{label}</p>
        <div className={`mt-2 text-xl font-bold tabular-nums tracking-tight sm:text-2xl ${accent ? 'text-emerald-800' : 'text-zinc-900'}`}>
          {value}
        </div>
      </div>
    </div>
  );
}

export type DashboardDetailDateProps = {
  initialDateRange?: DateRange;
};
